import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Button, Container, Alert, Row, Col } from "react-bootstrap";
import AdminSidebar from "../../../components/admin/sidebar/adminSidebar";
import ReactQuill from "react-quill";
import DOMPurify from "domPurify"; // HTML 정화를 위해 DOMPurify 사용
import axiosInstance from "../../../api/axios";
import "react-quill/dist/quill.snow.css";

export default function AdminOtherBusinessDetail() {
  const { otherBusinessId } = useParams(); // 현재 게시물 ID 가져오기
  const navigate = useNavigate(); // 페이지 이동을 위한 hook
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    attachmentPath: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef();

  // 게시물 데이터 로드
  useEffect(() => {
    const fetchOtherBusiness = async () => {
      try {
        const response = await axiosInstance.get(
          `/api/otherBusiness/detail/${otherBusinessId}`
        );
        const { title, content, attachmentPath } = response.data.data;
        setFormData({ title, content, attachmentPath });
      } catch (error) {
        console.error("Error fetching otherBusiness:", error);
        navigate("/admin/otherBusiness/list");
      }
    };

    fetchOtherBusiness();
  }, [otherBusinessId, navigate]);

  const handleTitleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleContentChange = (value) => {
    setFormData({ ...formData, content: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const maxSize = 10 * 1024 * 1024; // 5MB

    if (file) {
      if (file.size > maxSize) {
        alert("파일 크기가 10MB를 초과할 수 없습니다.");
        e.target.value = "";
      } else {
        const base64FileName = btoa(encodeURIComponent(file.name));

        axiosInstance
          .get("/api/otherBusiness/presigned-url", {
            params: { fileName: base64FileName, fileSize: file.size },
          })
          .then((res) => {
            const presignedUrl = res.data.data;

            return axiosInstance.put(presignedUrl, file, {
              headers: {
                "Content-Type": file.type || "application/octet-stream",
              },
              withCredentials: false,
            });
          })
          .then((uploadResponse) => {
            const uploadedPath = uploadResponse.config.url.split("?")[0];
            setFormData({ ...formData, attachmentPath: uploadedPath });
          })
          .catch((err) => {
            console.error("Error uploading file:", err);
            alert("파일 업로드 중 문제가 발생했습니다.");
          });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { title, content, attachmentPath } = formData;

    if (!title || !content) {
      setError("모든 필드를 입력해주세요.");
      return;
    }

    setError("");

    const sanitizedContent = DOMPurify.sanitize(content);

    try {
      await axiosInstance.put(`/api/otherBusiness/edit/${otherBusinessId}`, {
        title,
        content: sanitizedContent,
        attachmentPath,
      });

      setSuccess(true);
      navigate(`/admin/otherBusiness/${otherBusinessId}`);
    } catch (error) {
      console.error("Error updating otherBusiness:", error);
      setError("게시물 수정 중 오류가 발생했습니다.");
    }
  };

  const handleDownload = () => {
    if (formData.attachmentPath) {
      window.open(formData.attachmentPath, "_blank");
    } else {
      alert("첨부파일이 없습니다.");
    }
  };

  const handleImageUpload = async (file) => {
    try {
      const base64FileName = btoa(encodeURIComponent(file.name));
      const presignedUrlResponse = await axiosInstance.get(
        "/api/otherBusiness/presigned-url",
        {
          params: { fileName: base64FileName },
        }
      );
      const presignedUrl = presignedUrlResponse.data.data;

      await axiosInstance.put(presignedUrl, file, {
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        withCredentials: false,
      });

      return presignedUrl.split("?")[0]; // 업로드된 이미지의 URL 반환
    } catch (err) {
      console.error("Error uploading image:", err);
      alert("이미지 업로드 중 문제가 발생했습니다.");
      return null;
    }
  };

  useEffect(() => {
    const quillContainer = document.querySelector(".ql-container");
    if (!quillContainer || !quillContainer.__quill) {
      console.warn("Quill is not initialized yet.");
      return;
    }

    const quill = quillContainer.__quill;

    quill.getModule("toolbar").addHandler("image", () => {
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/*");
      input.click();

      input.onchange = async () => {
        const file = input.files[0];
        if (file) {
          const imageUrl = await handleImageUpload(file);
          if (imageUrl) {
            const range = quill.getSelection();
            quill.insertEmbed(range.index, "image", imageUrl);
          }
        }
      };
    });
  }, []);

  const quillModules = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  return (
    <AdminSidebar>
      <Container className="my-5">
        {formData.title ? (
          <>
            <h2 className="text-center mb-4">기타사업 상세보기 및 수정</h2>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && (
              <Alert
                variant="success"
                onClose={() => setSuccess(false)}
                dismissible
              >
                게시물이 성공적으로 수정되었습니다.
              </Alert>
            )}

            <Form onSubmit={handleSubmit} className="otherBusiness-detail-form">
              {/* 제목 입력 */}
              <Form.Group className="mb-3" controlId="formTitle">
                <Form.Label>제목</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="게시물 제목을 입력하세요"
                  name="title"
                  value={formData.title}
                  onChange={handleTitleChange}
                />
              </Form.Group>

              {/* 내용 입력 */}
              <Form.Group className="mb-3" controlId="formContent">
                <Form.Label>내용</Form.Label>
                {formData && (
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={handleContentChange}
                    modules={quillModules}
                    placeholder="게시물 내용을 입력하세요"
                  />
                )}
              </Form.Group>

              <Form.Group className="mb-3" controlId="formAttachment">
                <Form.Label>첨부파일</Form.Label>
                {formData.attachmentPath ? (
                  <div className="mb-2">
                    <Form.Control
                      type="text"
                      value={decodeURIComponent(
                        formData.attachmentPath.split("/").pop()
                      )}
                      readOnly
                    />
                    <Row className="mt-2">
                      <Col>
                        <Button
                          variant="outline-primary"
                          className="w-100"
                          onClick={handleDownload}
                        >
                          다운로드
                        </Button>
                      </Col>
                      <Col>
                        <Button
                          variant="outline-danger"
                          className="w-100"
                          onClick={() =>
                            setFormData({ ...formData, attachmentPath: null })
                          }
                        >
                          제거
                        </Button>
                      </Col>
                    </Row>
                  </div>
                ) : (
                  <Form.Control
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                  />
                )}
              </Form.Group>

              {/* 버튼 */}
              <div className="text-center">
                <Row>
                  <Col>
                    <Button
                      variant="secondary"
                      className="w-100"
                      onClick={() => navigate("/admin/otherBusiness/list")}
                    >
                      뒤로가기
                    </Button>
                  </Col>
                  <Col>
                    <Button variant="primary" type="submit" className="w-100">
                      저장하기
                    </Button>
                  </Col>
                </Row>
              </div>
            </Form>
          </>
        ) : (
          <p className="text-center">로딩 중...</p>
        )}
      </Container>
    </AdminSidebar>
  );
}
